import { useState } from "react";
import CustomTextInput from "../../../components/Input/Text/TextInput";
import classes from "./WorkExperience.module.css";
import DateInputDemo from "../../../components/Input/Date/Date";
import arrow from "../../../assets/arrow_from_to.svg";
import { useTranslation } from "../../../I18n/Languages/LanguageContext";

export default function WorkExperience({
  workExperience,
  updateWorkExperienceEntry,
  addWorkExperienceEntry,
  removeWorkExperienceEntry,
}) {
  const { t } = useTranslation();
  const [currentEditingIndex, setCurrentEditingIndex] = useState(0);
  const [savedEntries, setSavedEntries] = useState([]);

  const isEntryValid = (entry) => {
    if (!entry) return false;
    const requiredFields = ["employerName", "position"];
    return requiredFields.every(
      (field) => entry[field] && entry[field].trim().length > 0
    );
  };

  const handleAddExperience = () => {
    const currentEntry = workExperience[currentEditingIndex];

    if (currentEntry && isEntryValid(currentEntry)) {
      setSavedEntries((prev) => {
        if (!prev.includes(currentEditingIndex)) {
          return [...prev, currentEditingIndex];
        }
        return prev;
      });
    }

    addWorkExperienceEntry();

    setCurrentEditingIndex(workExperience.length);
  };

  const handleEdit = (index) => {
    setCurrentEditingIndex(index);
  };

  const handleDelete = (id) => {
    const deletedIndex = workExperience.findIndex((entry) => entry.id === id);

    setSavedEntries((prev) => prev.filter((index) => index !== deletedIndex));

    removeWorkExperienceEntry(id);

    const remainingEntries = workExperience.filter((entry) => entry.id !== id);

    if (remainingEntries.length === 0) {
      setCurrentEditingIndex(0);
    } else if (deletedIndex === currentEditingIndex) {
      setCurrentEditingIndex(0);
    } else if (deletedIndex < currentEditingIndex) {
      setCurrentEditingIndex(currentEditingIndex - 1);
    }

    setSavedEntries((prev) =>
      prev.map((index) => (index > deletedIndex ? index - 1 : index))
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleDateString();
  };

  const currentEntry = workExperience[currentEditingIndex] || {};

  const safeUpdateEntry = (field, value) => {
    if (!workExperience[currentEditingIndex]) {
      console.warn(
        `Attempting to update entry at index ${currentEditingIndex} but it doesn't exist`
      );
      return;
    }
    updateWorkExperienceEntry(currentEditingIndex, field, value);
  };

  return (
    <>
      <div className={classes.experienceHeader}>
        <div className={classes.h3red}>{t("last_work_experience")}</div>
        <div className={`${classes.buttonWrapper} ${classes.desktopOnly}`}>
          <button
            className={classes.applyBtnBottom}
            onClick={handleAddExperience}
          >
            {t("add_experience")}
          </button>
        </div>
      </div>
      <div className={classes.personalForm}>
        <div className={classes.firstGroup}>
          <CustomTextInput
            name="employerName"
            width="100%"
            label={t("employer_name")}
            topLabel={t("employer_name")}
            value={currentEntry?.employerName || ""}
            maxLength={100}
            onChange={(e) => safeUpdateEntry("employerName", e.target.value)}
          />
          <CustomTextInput
            name="position"
            width="100%"
            topLabel={t("job_title")}
            label={t("job_title")}
            value={currentEntry?.position || ""}
            maxLength={100}
            onChange={(e) => safeUpdateEntry("position", e.target.value)}
          />
        </div>

        <div className={classes.firstGroup}>
          <div className={classes.dateGroup}>
            <DateInputDemo
              topLabel={t("from")}
              width="100%"
              value={currentEntry?.startDate || ""}
              onChange={(e) => safeUpdateEntry("startDate", e.target.value)}
            />
            <img alt="arrow" className={classes.dateArrow} src={arrow} />
            <DateInputDemo
              width="100%"
              topLabel={t("to")}
              value={currentEntry?.endDate || ""}
              onChange={(e) => safeUpdateEntry("endDate", e.target.value)}
            />
          </div>

          <CustomTextInput
            name="employmentReasonForLeaving"
            topLabel={t("reason_for_leaving")}
            width="100%"
            label={t("reason_for_leaving")}
            value={currentEntry?.employmentReasonForLeaving || ""}
            maxLength={200}
            onChange={(e) =>
              safeUpdateEntry("employmentReasonForLeaving", e.target.value)
            }
          />
        </div>
        <div className={classes.firstGroup}>
          <CustomTextInput
            name="responsibilities"
            topLabel={t("main_responsibilities")}
            label={t("main_responsibilities")}
            multiline={true}
            rows={4}
            value={currentEntry?.responsibilities || ""}
            maxLength={500}
            onChange={(e) =>
              safeUpdateEntry("responsibilities", e.target.value)
            }
          />
        </div>
        <div className={`${classes.buttonWrapper} ${classes.mobileOnly}`}>
          <button
            className={classes.applyBtnBottom}
            onClick={handleAddExperience}
          >
            {t("add_experience")}
          </button>
        </div>
      </div>

      {savedEntries.length > 0 && (
        <div className={classes.summarySection}>
          {savedEntries
            .map((entryIndex) => workExperience[entryIndex])
            .filter(Boolean)
            .map((entry, displayIndex) => {
              const actualIndex = savedEntries[displayIndex];

              return (
                <div
                  key={entry.id}
                  className={`${classes.experienceSummary} ${
                    actualIndex === currentEditingIndex
                      ? classes.activeSummary
                      : ""
                  }`}
                >
                  <div className={classes.summaryContent}>
                    <div className={classes.summaryHeader}>
                      <h4 className={classes.positionTitle}>
                        {entry.position || t("position_not_specified")}
                      </h4>
                      <span className={classes.company}>
                        {entry.employerName || t("employer_not_specified")}
                      </span>
                    </div>

                    <div className={classes.summaryDetails}>
                      <span className={classes.dateRange}>
                        {formatDate(entry.startDate)} -{" "}
                        {formatDate(entry.endDate)}
                      </span>
                      {entry.employmentReasonForLeaving && (
                        <span className={classes.location}>
                          {entry.employmentReasonForLeaving}
                        </span>
                      )}
                    </div>
                    <div className={classes.responsibilities}>
                      {entry.responsibilities}
                    </div>
                  </div>

                  <div className={classes.actionButtons}>
                    <button
                      className={classes.editBtn}
                      onClick={() => handleEdit(actualIndex)}
                    >
                      {t("edit")}
                    </button>
                    <button
                      className={classes.deleteBtn}
                      onClick={() => handleDelete(entry.id)}
                    >
                      {t("delete")}
                    </button>
                  </div>
                </div>
              );
            })}
        </div>
      )}
    </>
  );
}
